import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

/* ================= LOADING SPINNER ================= */
const LoadingSpinner = ({ text = "Loading data…" }) => (
  <div
    className="d-flex flex-column justify-content-center align-items-center py-5"
    style={{ minHeight: "300px" }}
  >
    <div
      className="spinner-border text-warning"
      role="status"
      style={{ width: "3rem", height: "3rem" }}
      aria-label="Loading"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
    <p className="mt-3 text-muted fw-semibold">{text}</p>
  </div>
);

function PowerGenPlantListing() {
  const [plants, setPlants] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const plantsPerPage = 6;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const slugify = (text = "") =>
    text
      .toLowerCase()
      .trim()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");

  /* ================= FETCH PLANTS ================= */
  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          "https://backend-zkgn.onrender.com/api/plants",
        );
        setPlants(res.data || []);
      } catch (err) {
        console.error("Error fetching plants:", err);
        setError("Failed to load power generation plants");
        setPlants([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlants();
  }, []);

  /* ================= FILTER ================= */
  const filteredPlants = plants.filter((plant) => {
    const name = plant.genplant_name?.toLowerCase() || "";
    const city = plant.genplant_city?.toLowerCase() || "";
    return (
      name.includes(search.toLowerCase()) || city.includes(search.toLowerCase())
    );
  });

  /* ================= PAGINATION ================= */
  const totalPages = Math.ceil(filteredPlants.length / plantsPerPage);
  const indexOfLast = currentPage * plantsPerPage;
  const indexOfFirst = indexOfLast - plantsPerPage;
  const currentPlants = filteredPlants.slice(indexOfFirst, indexOfLast);

  const paginate = (page) => setCurrentPage(page);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  return (
    <div className="container-fluid my-5">
      <div className="row">
        <div className="col-lg-12">
          {/* ================= COUNT + SEARCH ================= */}
          <div className="row align-items-center mb-3">
            <div className="col-md-6 mb-2 mb-md-0">
              {!loading && !error && (
                <h5 className="fw-bold text-tertiry mb-0">
                  <strong>{filteredPlants.length}</strong> Power generation
                  plants found
                </h5>
              )}
            </div>

            <div className="col-md-6">
              <input
                type="text"
                className="form-control search-box w-100"
                placeholder="Search by name or city..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                disabled={loading}
              />
            </div>
          </div>

          {/* ================= PLANT LIST ================= */}
          {loading ? (
            <LoadingSpinner text="Fetching power generation plants…" />
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : (
            <div className="row g-4">
              {currentPlants.length > 0 ? (
                currentPlants.map((plant) => (
                  <div className="col-md-6" key={plant.power_id}>
                    <div className="card shadow-sm plant-card h-100">
                      <div className="card-body">
                        <Link
                          className="plant-link"
                          to={`/powergeneration/${slugify(
                            plant.genplant_name,
                          )}/${plant.power_id}`}
                        >
                          <h5 className="card-title fw-bold text-dark">
                            {plant.genplant_name}
                          </h5>
                        </Link>

                        <p className="mb-1">
                          <strong className="text-orange">Address:</strong>{" "}
                          {plant.genplant_address || "N/A"}
                        </p>

                        <p className="mb-0">
                          <strong className="text-orange">Contact:</strong>{" "}
                          {plant.genplant_phone || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="alert alert-warning text-center w-100">
                  No plants found matching your search.
                </div>
              )}
            </div>
          )}

          {/* ================= PAGINATION ================= */}
          {totalPages > 1 && !loading && (
            <nav className="d-flex justify-content-center mt-4">
              <ul className="pagination pagination-sm flex-wrap shadow-sm">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button className="page-link" onClick={goToPreviousPage}>
                    ← Previous
                  </button>
                </li>

                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <li key={index} className="page-item disabled">
                      <span className="page-link">...</span>
                    </li>
                  ) : (
                    <li
                      key={index}
                      className={`page-item ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => paginate(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ),
                )}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button className="page-link" onClick={goToNextPage}>
                    Next →
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
}

export default PowerGenPlantListing;
